import React, { useRef, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useOnClickOutside } from "usehooks-ts";

import { type MetricFilters } from "@features/sales-v3/hooks/use-chart-state";
import { IconButton } from "@shared/buttons";

import { FiltersTooltip } from "./manage-filters-button-tooltip";

interface ManageFiltersButtonProps {
  metricFilters: MetricFilters[];
  ignoreTableFilters: boolean;
  onClick?: () => void;
  isMenuOpen: boolean;
  isDisabled?: boolean;
  removeAllMetricFilters: () => void;
}

export const ManageFiltersButton = ({
  metricFilters,
  ignoreTableFilters,
  onClick,
  isMenuOpen,
  isDisabled = false,
  removeAllMetricFilters,
}: ManageFiltersButtonProps) => {
  const displaySelectedFiltersText = metricFilters
    .reduce<string[]>((acc, { breakdown, values }) => {
      const count = values.length;
      const filterName = breakdown.name;
      acc.push(`(${count}) ${filterName}`);
      return acc;
    }, [])
    .join(", ");

  const [isDeletingMetric, setIsDeletingMetric] = useState(false);
  const undoIsDeletingMetric = () => setIsDeletingMetric(false);

  const hasSelectedFilters = metricFilters.length > 0;

  return (
    <FiltersTooltip
      canShowTooltip={!isMenuOpen && !isDeletingMetric}
      metricFilters={metricFilters}
      ignoreTableFilters={ignoreTableFilters}
    >
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        {isDeletingMetric ? (
          <DeleteMetricFilterButton
            undoIsDeletingMetric={undoIsDeletingMetric}
            removeAllMetricFilters={removeAllMetricFilters}
          />
        ) : (
          <IconButton
            className={clsx(
              { "bg-cosmic text-white": isMenuOpen },
              {
                "bg-cosmic-3 text-interstellar border-cosmic":
                  hasSelectedFilters,
                "hover:bg-cosmic hover:text-white": !isDisabled,
              },
              "px-2 py-1 font-medium border border-transparent group/item",
            )}
            disabled={isDisabled}
            variant="tertiary"
            onClick={() => onClick?.()}
          >
            {hasSelectedFilters ? (
              <div className="flex items-center space-x-2">
                <div
                  className={clsx(
                    "w-16 text-left truncate group-hover/item:text-white",
                    "text-interstellar",
                  )}
                >
                  {displaySelectedFiltersText}
                </div>
                <span
                  className="group/delete"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsDeletingMetric(true);
                  }}
                >
                  <div className="rounded-full group-hover/delete:bg-white p-2 relative">
                    <i
                      className={clsx(
                        "fa-regular fa-sharp fa-xmark group-hover/item:text-white",
                        "text-interstellar group-hover/delete:hover:!text-cosmic absolute",
                        "leading-0",
                        "w-4 h-4 transform -translate-x-1/2",
                      )}
                    />
                  </div>
                </span>
              </div>
            ) : (
              <>
                <i
                  className={clsx(
                    {
                      "bg-cosmic text-white": isMenuOpen,
                      "group-hover/item:text-white": !isDisabled,
                    },
                    "fa-regular fa-sharp fa-circle-plus mr-1",
                  )}
                />
                ADD FILTER
              </>
            )}
          </IconButton>
        )}
      </motion.span>
    </FiltersTooltip>
  );
};

const DeleteMetricFilterButton = ({
  undoIsDeletingMetric,
  removeAllMetricFilters,
}: {
  undoIsDeletingMetric: () => void;
  removeAllMetricFilters: () => void;
}) => {
  const ref = useRef(null);
  useOnClickOutside(ref, undoIsDeletingMetric);

  return (
    <IconButton
      ref={ref}
      className={clsx(
        "bg-mars-2 inline-flex items-center space-between",
        "border border-transparent px-2 py-1",
      )}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <span className="mr-3">Delete filter?</span>
      <div>
        <span
          className="font-semibold text-mars mr-2"
          onClick={() => {
            undoIsDeletingMetric();
            removeAllMetricFilters();
          }}
        >
          YES
        </span>
        <span
          className="font-semibold text-cosmic"
          onClick={undoIsDeletingMetric}
        >
          NO
        </span>
      </div>
    </IconButton>
  );
};
